import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CreateGiftAllocate,
  FetchMasterGiftAction,
  FetchGiftsBySchemeAction,
} from "../../redux/actions/GiftAction";
import { FaTrash, FaPlus, FaTimes } from "react-icons/fa";

const AllocateGiftsModal = ({ isOpen, onClose, schemes, orderId }) => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { loading, error, GiftList } = useSelector((state) => state.gift);

  const [gifts, setGifts] = useState([
    {
      scheme_name: "",
      gift_id: "",
      qr_count: 0,
      is_additional_gift: false,
     
    },
  ]);

  const [errors, setErrors] = useState({});
  const [schemeGifts, setSchemeGifts] = useState({});
  const [masterLoading, setMasterLoading] = useState(false);
  const [giftLoadingByScheme, setGiftLoadingByScheme] = useState({});


  // Fetch master schemes on component mount
  useEffect(() => {
    if (isOpen && userInfo?.access_token) {
      setMasterLoading(true);
      dispatch(FetchMasterGiftAction(userInfo.access_token))
        .then(() => {
          setMasterLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching master gifts:", err);
          setMasterLoading(false);
        });
    }
  }, [isOpen, dispatch, userInfo?.access_token]);



  // Fetch gifts for a specific scheme
  const fetchGiftsForScheme = (schemeName) => {
    if (!schemeName || schemeGifts[schemeName]) {
      return;
    }

    setGiftLoadingByScheme((prev) => ({
      ...prev,
      [schemeName]: true,
    }));

    dispatch(FetchGiftsBySchemeAction(schemeName, userInfo.access_token))
      .then((response) => {
        const giftsData = response?.data?.data || response?.data || [];
        setSchemeGifts((prev) => ({
          ...prev,
          [schemeName]: giftsData,
        }));
        setGiftLoadingByScheme((prev) => ({
          ...prev,
          [schemeName]: false,
        }));
      })
      .catch((err) => {
        console.error(`Error fetching gifts for scheme ${schemeName}:`, err);
        setGiftLoadingByScheme((prev) => ({
          ...prev,
          [schemeName]: false,
        }));
      });
  };

  const handleGiftChange = (index, field, value) => {
    const updatedGifts = [...gifts];
    if (field === "qr_count") {
      updatedGifts[index][field] = parseInt(value) || 0;
    } else if (field === "scheme_name") {
      updatedGifts[index][field] = value;
      updatedGifts[index].gift_id = "";
      if (value) {
        fetchGiftsForScheme(value);
      }
    } else if (field === "is_additional_gift") {
      updatedGifts[index][field] = value;
    } else {
      updatedGifts[index][field] = value;
    }
    setGifts(updatedGifts);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${field}_${index}`];
      return newErrors;
    });
  };
  const toggleCityForGift = (index, city) => {
    const updatedGifts = [...gifts];
    const currentCities = updatedGifts[index].cities;
    updatedGifts[index].cities = currentCities.includes(city)
      ? currentCities.filter((c) => c !== city)
      : [...currentCities, city];
    setGifts(updatedGifts);
  };
  const addGift = () => {
    setGifts([
      ...gifts,
      {
        scheme_name: "",
        gift_id: "",
        qr_count: 0,
        is_additional_gift: false,
        cities: "",
      },
    ]);
  };

  const removeGift = (index) => {
    if (gifts.length === 1) {
      alert("At least one gift is required");
      return;
    }
    const updatedGifts = gifts.filter((_, i) => i !== index);
    setGifts(updatedGifts);
  };

  const validateForm = () => {
    const newErrors = {};

    gifts.forEach((gift, index) => {
      if (!gift.scheme_name.trim()) {
        newErrors[`scheme_name_${index}`] = "Scheme is required";
      }
      if (!gift.gift_id) {
        newErrors[`gift_id_${index}`] = "Gift is required";
      }
      if (!gift.qr_count || gift.qr_count <= 0) {
        newErrors[`qr_count_${index}`] = "QR count must be greater than 0";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert("Please fix all errors before submitting");
      return;
    }

    const payload = {
      qr_scheme: schemes,
      gifts: gifts.map((gift) => ({
        gift_id: gift.gift_id,
        qr_count: gift.qr_count,
        is_additional_gift: gift.is_additional_gift,
      })),
    };
    
    try {
      const result = await dispatch(
        CreateGiftAllocate(payload, userInfo.access_token),
      );
      if (result.type === "CREATE_GIFT_CREATE_SUCCESS") {
        // alert("Gift allocation created successfully!");
        setGifts([
          {
            scheme_name: "",
            gift_id: "",
            qr_count: 0,
            is_additional_gift: false,
            cities: "",
          },
        ]);
        setErrors({});
        onClose();
      } else {
        alert("Failed to create gift allocation");
      }
    } catch (err) {
      console.error("Error creating gift allocation:", err);
      alert("Error creating gift allocation");
    }
  };

  const getGiftsForScheme = (schemeName) => {
    return schemeGifts[schemeName] || [];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-amber-600">Allocate Gifts</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-950 mb-4 flex items-center gap-2">
              <FaPlus className="text-amber-600" /> Add Gift Allocations
            </h3>

            <div className="space-y-4 mb-6">
              {gifts.map((gift, index) => (
                <div
                  key={index}
                  className="border-2 border-gray-200 rounded-lg p-5 hover:border-amber-300 transition bg-gray-50"
                >
                  <div className="flex justify-between items-start mb-4">
                    <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-semibold">
                      Allocation #{index + 1}
                    </span>
                    {gifts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeGift(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition"
                        title="Remove allocation"
                      >
                        <FaTrash size={18} />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Scheme Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Scheme <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={gift.scheme_name}
                        onChange={(e) =>
                          handleGiftChange(index, "scheme_name", e.target.value)
                        }
                        disabled={masterLoading}
                        className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition ${
                          errors[`scheme_name_${index}`]
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-300 focus:border-amber-500"
                        } ${masterLoading ? "bg-gray-100" : ""}`}
                      >
                        <option value="">
                          {masterLoading
                            ? "Loading schemes..."
                            : "Select Scheme"}
                        </option>
                        {GiftList &&
                          GiftList.map((scheme) => (
                            <option
                              key={scheme.scheme_name}
                              value={scheme.scheme_name}
                            >
                              {scheme.scheme_name}
                            </option>
                          ))}
                      </select>
                      {errors[`scheme_name_${index}`] && (
                        <p className="text-red-500 text-xs mt-1 font-medium">
                          {errors[`scheme_name_${index}`]}
                        </p>
                      )}
                    </div>

                    {/* Gift Dropdown */}
                    {/* <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gift <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={gift.gift_id}
                        onChange={(e) =>
                          handleGiftChange(index, "gift_id", e.target.value)
                        }
                        disabled={
                          !gift.scheme_name ||
                          giftLoadingByScheme[gift.scheme_name]
                        }
                        className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition ${
                          errors[`gift_id_${index}`]
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-300 focus:border-amber-500"
                        } ${
                          !gift.scheme_name ||
                          giftLoadingByScheme[gift.scheme_name]
                            ? "bg-gray-100 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <option value="">
                          {!gift.scheme_name
                            ? "Select Scheme First"
                            : giftLoadingByScheme[gift.scheme_name]
                            ? "Loading gifts..."
                            : "Select Gift"}
                        </option>
                        {gift.scheme_name &&
                          getGiftsForScheme(gift.scheme_name).map(
                            (giftItem) => (
                              <option
                                key={giftItem.gift_id}
                                value={giftItem.gift_id}
                              >
                                {giftItem.gift_name}
                              </option>
                            )
                          )}
                      </select>
                      {errors[`gift_id_${index}`] && (
                        <p className="text-red-500 text-xs mt-1 font-medium">
                          {errors[`gift_id_${index}`]}
                        </p>
                      )}
                    </div> */}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gift <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={gift.gift_id}
                        onChange={(e) =>
                          handleGiftChange(index, "gift_id", e.target.value)
                        }
                        disabled={
                          !gift.scheme_name ||
                          giftLoadingByScheme[gift.scheme_name]
                        }
                        className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition ${
                          errors[`gift_id_${index}`]
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-300 focus:border-amber-500"
                        } ${
                          !gift.scheme_name ||
                          giftLoadingByScheme[gift.scheme_name]
                            ? "bg-gray-100 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <option value="">
                          {!gift.scheme_name
                            ? "Select Scheme First"
                            : giftLoadingByScheme[gift.scheme_name]
                              ? "Loading gifts..."
                              : "Select Gift"}
                        </option>
                        {gift.scheme_name &&
                          getGiftsForScheme(gift.scheme_name)
                            .filter((giftItem) => giftItem.is_live === true)
                            .map((giftItem) => (
                              <option
                                key={giftItem.gift_id}
                                value={giftItem.gift_id}
                              >
                                {giftItem.gift_name}
                              </option>
                            ))}
                      </select>
                      {errors[`gift_id_${index}`] && (
                        <p className="text-red-500 text-xs mt-1 font-medium">
                          {errors[`gift_id_${index}`]}
                        </p>
                      )}
                    </div>
                    {/* QR Count */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        QR Count <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        placeholder="e.g., 10"
                        value={gift.qr_count}
                        onChange={(e) =>
                          handleGiftChange(index, "qr_count", e.target.value)
                        }
                        className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition ${
                          errors[`qr_count_${index}`]
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-300 focus:border-amber-500"
                        }`}
                        min="1"
                      />
                      {errors[`qr_count_${index}`] && (
                        <p className="text-red-500 text-xs mt-1 font-medium">
                          {errors[`qr_count_${index}`]}
                        </p>
                      )}
                    </div>
                  </div>
                
                  {/* Bumper Offer Checkbox */}
                  <div className="mt-4 flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200">
                    <input
                      type="checkbox"
                      id={`bumper_offer_${index}`}
                      checked={gift.is_additional_gift}
                      onChange={(e) =>
                        handleGiftChange(
                          index,
                          "is_additional_gift",
                          e.target.checked,
                        )
                      }
                      className="w-5 h-5 text-amber-600 rounded focus:ring-2 focus:ring-amber-500 cursor-pointer"
                    />
                    <label
                      htmlFor={`bumper_offer_${index}`}
                      className="text-sm font-medium text-gray-700 cursor-pointer flex items-center gap-2"
                    >
                      <span>Additional Gift (Bumper Offer)</span>
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
                        Optional
                      </span>
                    </label>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Gift Button */}
            <button
              type="button"
              onClick={addGift}
              className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium transition px-4 py-2 rounded-lg hover:bg-amber-50 mb-6"
            >
              <FaPlus size={18} /> Add More Allocation
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border-2 border-red-300 text-red-700 p-4 rounded-lg">
              <p className="font-semibold">Error:</p>
              <p className="text-sm">
                {typeof error === "string" ? error : JSON.stringify(error)}
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-4 pt-6 border-t-2 border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-6 rounded-lg font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-amber-600 hover:bg-amber-700 text-white py-2 px-8 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Submitting..." : "Submit Gift Allocation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AllocateGiftsModal;
